import React, { useMemo, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import NodePill from 'components/NodePill';
import icons from 'img/icons';
import Tooltip from 'components/Tooltip';
import text from 'constants/text';
import { StickyTable, Row, Cell } from 'react-sticky-table';
import * as d3 from 'd3';
import './AbundanceView.scss';

const AbundanceArrow = ({ node, change, fixedResults }) => {
  const arrowHeadWidth = 6;

  const markerWidth = arrowHeadWidth;

  const svgElement = useRef(null);
  const svgCell = useRef(null);

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const greatestExaggeration = Math.max(
    node?.matrixExaggeration,
    node?.experimentExaggeration
  );

  const [arrow, setArrow] = useState({
    start: change === 'node-added' ? 0 : node.matrixAbundance,
    end: change === 'node-deleted' ? 0 : node.experimentAbundance,
  });

  useEffect(() => {
    setArrow({
      start: change === 'node-added' ? 0 : node.matrixAbundance,
      end: change === 'node-deleted' ? 0 : node.experimentAbundance,
    });

    //Watches for table resizing
    const observer = new ResizeObserver(entries => {
      if (!entries.length) {
        return;
      }
      handleResize(entries);
    });
    observer.observe(svgCell.current);

    d3.select(svgElement.current)
      .selectAll('g')
      .remove();

    const svgHeight = 18;
    const lineHeight = 6;

    const svg = d3.select(svgElement.current);
    svg.style('height', svgHeight + 'px').append('g');

    const circleRadius = lineHeight / 2;

    //more margin if it is a circle so they dont get cutoff
    const hMargin = (node, change, circleMargin, defaultMargin) => {
      if (change === 'no-change') {
        return circleMargin;
      }

      if (change === 'node-added' && node.experimentPopulation === 0) {
        return circleMargin;
      }

      if (change === 'node-deleted' && node.matrixPopulation === 0) {
        return circleMargin;
      }

      return defaultMargin;
    };

    let horizontalMargin = hMargin(node, change, circleRadius, 1);

    const verticalMargin = 0;
    const frameWidth = width - horizontalMargin * 2;
    const frameHeight = svgHeight - verticalMargin * 2;

    const roundCapMargin = 0.025;

    // Adds a little wiggle room on each side for round caps
    var xScale = d3.scaleLinear().range([horizontalMargin, frameWidth]);
    xScale.domain([-1 * roundCapMargin, greatestExaggeration + roundCapMargin]);

    const y_value = frameHeight / 2;

    svg
      .select('g')
      .append('line')
      .attr('x1', xScale(0))
      .attr('y1', y_value)
      .attr('x2', xScale(greatestExaggeration - roundCapMargin))
      .attr('y2', y_value)
      .attr('stroke-width', lineHeight)
      .classed('background-line', true)
      .attr('stroke-linecap', 'butt');

    if (node.matrixPopulation === node.experimentPopulation) {
      svg
        .select('g')
        .append('circle')
        .classed('no-change-dot', true)
        .attr('cx', d => xScale(node.matrixAbundance))
        .attr('cy', y_value)
        .attr('r', circleRadius);
    } else {
      //extra margin to keep arrow in cell for small increase from 0 or small decrease from max
      let addedMargin =
        xScale(arrow.end) < markerWidth && change === 'increase'
          ? markerWidth - xScale(arrow.end)
          : 0;
      let subtractedMargin =
        xScale(arrow.end) > frameWidth - markerWidth && change === 'decrease'
          ? markerWidth - (frameWidth - xScale(arrow.end))
          : 0;

      const startLoc = xScale(arrow.start) + addedMargin - subtractedMargin;
      const endLoc = xScale(arrow.end) + addedMargin - subtractedMargin;

      let halfwayPoint = 0;
      if (endLoc > startLoc) {
        halfwayPoint = (endLoc - startLoc) / 2 + startLoc;
      } else {
        halfwayPoint = (startLoc - endLoc) / 2 + endLoc;
      }

      svg
        .select('g')
        .append('line')
        .attr('x1', startLoc)
        .attr('y1', y_value)
        .attr('x2', halfwayPoint)
        .attr('y2', y_value)
        .attr('stroke-width', lineHeight)
        .attr('stroke-linecap', 'butt');

      svg
        .select('g')
        .append('line')
        .attr('x1', halfwayPoint)
        .attr('y1', y_value)
        .attr('x2', endLoc)
        .attr('y2', y_value)
        .attr('stroke-width', lineHeight)
        .attr('stroke-linecap', 'round');

      svg.attr(
        'marker-end',
        `url(#arrow_${arrow.start > arrow.end ? 'left' : 'right'})`
      );

      // start flag marker
      svg
        .select('g')
        .append('line')
        .attr('x1', xScale(arrow.start) + addedMargin - subtractedMargin)
        .attr('y1', y_value - 15)
        .attr('x2', xScale(arrow.start) + addedMargin - subtractedMargin)
        .attr('y2', y_value + 15)
        .attr('stroke-width', 1)
        .attr('stroke', 'black')
        .attr('stroke-linecap', 'round');
    }

    return () => {
      observer.disconnect();
    };

    function handleResize(entries) {
      for (const entry of entries) {
        setHeight(entry.contentRect.height);
        setWidth(entry.contentRect.width);
      }
    }
  }, [svgCell, width, height, change, node, markerWidth]);

  return (
    <div ref={svgCell} className="change-abundance">
      <div className="AbundanceArrow-svg-wrap">
        <svg ref={svgElement}></svg>
      </div>
    </div>
  );
};

const AbundanceRow = ({ node, headers, fixed, pointOfComparison }) => {
  // TODO when switching an output to an input, need to show the before as non-fixed and after as fixed for population numbers
  // useEffect(() => console.log(node), [node]);

  const modifiedAbundanceChange = (
    matrixPopulation,
    experimentPopulation,
    fixedResults
  ) => {
    if (matrixPopulation === experimentPopulation) {
      return null;
    } else if (fixedResults) {
      return 'modFixed';
    } else if (matrixPopulation > experimentPopulation) {
      return 'modDecrease';
    } else if (matrixPopulation < experimentPopulation) {
      return 'modIncrease';
    }
  };

  return (
    <Row className={classNames('abundance-row', node.change)}>
      <Cell className="name">
        <div className="node-name-image">
          {node?.imageUrl ? (
            <img src={node?.imageUrl} />
          ) : (
            <div className="node-name-image-fallback">
              <img src={node?.fallbackImageUrl} />
            </div>
          )}
        </div>
      </Cell>
      <Cell className="name">
        <div className="cell-container">
          <div className="node-name-container">
            <div className="node-name-column">
              <div className="node-name-top-container">
                <div className="node-name-text">{node?.name}</div>
                <div className="node-name-text">
                  <span className="node-name-text-original-abundance">
                    {node.matrixPopulation ? node.matrixPopulation : 0} →
                  </span>
                  <span
                    className={classNames(
                      'node-name-text-new-abundance',
                      modifiedAbundanceChange(
                        node.matrixPopulation,
                        node.experimentPopulation,
                        pointOfComparison === 'results' ? fixed : false
                      )
                    )}
                  >
                    {node.experimentPopulation ||
                    node.experimentPopulation === 0
                      ? node.experimentPopulation
                      : 0}
                  </span>
                </div>
              </div>

              <div className="abundance-arrow-container">
                <AbundanceArrow
                  node={node}
                  change={node.change}
                  fixedResults={pointOfComparison === 'results' ? fixed : false}
                ></AbundanceArrow>
              </div>
            </div>
          </div>
        </div>
      </Cell>

      <Cell className={headers[1].class}>
        <div className="cell-container">{node.populationUnit}</div>
      </Cell>
      <Cell className={headers[2].class}>
        <div className="cell-container">{node.percentChange}</div>
      </Cell>
      <Cell
        className={classNames(
          headers[3].class,
          node.matrixCategory === node.experimentCategory
            ? 'not-significant'
            : null
        )}
      >
        <div className="cell-container">{node.changeDisplay}</div>
      </Cell>
      <Cell className={classNames(headers[4].class)}>
        <div className="cell-container">
          {node.fixed ? <div className="fixed">input</div> : 'output'}
        </div>
      </Cell>
    </Row>
  );
};

const AbundanceTable = ({ nodes, pointOfComparison }) => {
  const headers = [
    // trick to allow horizontal scroll appropriately
    {
      display: 'Node',
      class: 'image',
    },
    {
      display: '',
      class: 'name',
      tooltip: text.tooltip.abundanceView.name,
      tooltipAlign: 'right',
    },
    { display: 'Units', class: 'units' },
    { display: 'Percent change', class: 'percent-change' },
    {
      display: 'Fuzzy change',
      class: 'fuzzy-change',
      tooltip: text.tooltip.abundanceView.fuzzyChange,
      tooltipAlign: 'left',
    },
    {
      display: 'Status',
      class: 'status',
    },
  ];

  return (
    <div className="AbundanceTable">
      <StickyTable id="charts-download" leftColumns={0}>
        <Row>
          {headers.map(heading => (
            <Cell key={heading.display} className={`heading ${heading.class}`}>
              <div className="name-tooltip">
                {heading.display}
                {heading.tooltip ? (
                  <Tooltip left={heading.tooltipAlign === 'left'}>
                    {heading.tooltip}
                  </Tooltip>
                ) : null}
              </div>
            </Cell>
          ))}
        </Row>

        {nodes.map(node => (
          <AbundanceRow
            key={node.id}
            node={node}
            headers={headers}
            fixed={node.fixed}
            pointOfComparison={pointOfComparison}
          ></AbundanceRow>
        ))}
      </StickyTable>
    </div>
  );
};

export default AbundanceTable;
